import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, currency, formatApiErrorDetail } from "../lib/api";
import { useServicesLabel } from "../context/SettingsContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { ArrowLeft, Plus, Wrench, Trash2, Package } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { value: "in_inventory", label: "In Inventory" },
  { value: "pending", label: "Pending" },
  { value: "maintenance", label: "Maintenance" },
  { value: "sold", label: "Sold" },
];

const statusMeta = {
  in_inventory: { label: "In Inventory", className: "text-blue-700" },
  pending: { label: "Pending", className: "text-amber-700" },
  maintenance: { label: "Maintenance", className: "text-violet-700" },
  sold: { label: "Sold", className: "text-emerald-700" },
};

const EXPENSE_GROUPS = [
  { key: "parts", label: "Parts", match: (e) => e.category === "part" },
  { key: "labor", label: "Labor", match: (e) => e.category === "labor" },
  {
    key: "general",
    label: "General",
    match: (e) => !["part", "labor"].includes(e.category),
  },
];

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const svcLabel = useServicesLabel();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [vehicle, setVehicle] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [usages, setUsages] = useState([]);
  const [parts, setParts] = useState([]);
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);

  const [expOpen, setExpOpen] = useState(false);
  const [expForm, setExpForm] = useState({ description: "", amount: 0, category: "general", service_id: "" });

  const [partOpen, setPartOpen] = useState(false);
  const [selectedPart, setSelectedPart] = useState("");
  const [partQty, setPartQty] = useState(1);
  const [cartItems, setCartItems] = useState([]); // [{part_id, quantity, name, unit_cost}]

  const loadAll = async () => {
    const [v, ex, us, ps, sv] = await Promise.all([
      api.get(`/vehicles/${id}`),
      api.get(`/vehicles/${id}/expenses`),
      api.get(`/vehicles/${id}/part-usages`),
      api.get(`/parts`),
      api.get(`/services`),
    ]);
    setVehicle(v.data);
    setExpenses(ex.data);
    setUsages(us.data);
    setParts(ps.data);
    setServices(sv.data);
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onEditSave = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...editing,
        year: Number(editing.year),
        mileage: Number(editing.mileage || 0),
        purchase_price: Number(editing.purchase_price || 0),
        sale_price: editing.sale_price === "" || editing.sale_price == null ? null : Number(editing.sale_price),
        sold_date: editing.sold_date || null,
        list_month: editing.list_month || null,
        title: editing.title || "",
      };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.total_expenses;
      delete payload.profit;
      delete payload.customer_matches;
      const { data: updated } = await api.patch(`/vehicles/${id}`, payload);
      const matches = updated?.customer_matches || [];
      if (matches.length > 0) {
        const names = matches.map((m) => m.customer?.name).filter(Boolean).join(", ");
        toast.info(
          `Matches ${matches.length} customer request${matches.length > 1 ? "s" : ""}: ${names}`,
          { duration: 6000 },
        );
      }
      toast.success("Vehicle updated");
      setEditing(null);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const onDeleteVehicle = async () => {
    if (!window.confirm("Delete this vehicle and all its records?")) return;
    try {
      await api.delete(`/vehicles/${id}`);
      toast.success("Vehicle deleted");
      navigate("/vehicles");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addExpense = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        description: expForm.description,
        amount: Number(expForm.amount),
        category: expForm.category,
      };
      if (expForm.service_id) payload.service_id = expForm.service_id;
      const { data } = await api.post(`/vehicles/${id}/expenses`, payload);
      toast.success("Expense added");
      const autos = data?.auto_usages || [];
      const warns = data?.warnings || [];
      if (autos.length > 0) {
        toast.success(`Auto-deducted ${autos.length} linked part${autos.length > 1 ? "s" : ""} from inventory`);
      }
      warns.forEach((w) => toast.warning(w));
      setExpOpen(false);
      setExpForm({ description: "", amount: 0, category: "general", service_id: "" });
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const onServiceSelect = (val) => {
    if (val === "__none__") {
      setExpForm({ ...expForm, service_id: "" });
      return;
    }
    const svc = services.find((s) => s.id === val);
    if (!svc) return;
    setExpForm({
      ...expForm,
      service_id: val,
      description: svc.name,
      amount: svc.default_cost,
      category: "labor",
    });
  };

  const deleteExpense = async (eid) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await api.delete(`/vehicles/${id}/expenses/${eid}`);
      toast.success("Deleted");
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deletePartUsage = async (usage) => {
    if (!window.confirm(`Remove ${usage.quantity}x "${usage.part_name}" from this vehicle and add it back to inventory?`)) return;
    try {
      await api.delete(`/vehicles/${id}/part-usages/${usage.id}`);
      toast.success(`Restored ${usage.quantity} to inventory`);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addToCart = () => {
    if (!selectedPart || partQty < 1) return;
    const part = parts.find((p) => p.id === selectedPart);
    if (!part) return;
    setCartItems((prev) => {
      const existing = prev.find((i) => i.part_id === selectedPart);
      if (existing) {
        return prev.map((i) =>
          i.part_id === selectedPart ? { ...i, quantity: i.quantity + Number(partQty) } : i,
        );
      }
      return [
        ...prev,
        { part_id: selectedPart, quantity: Number(partQty), name: part.name, unit_cost: part.unit_cost, sku: part.sku },
      ];
    });
    setSelectedPart("");
    setPartQty(1);
  };

  const submitUsage = async () => {
    if (cartItems.length === 0) {
      toast.error("Add at least one part");
      return;
    }
    try {
      await api.post(`/vehicles/${id}/use-parts`, {
        items: cartItems.map((i) => ({ part_id: i.part_id, quantity: i.quantity })),
      });
      toast.success("Parts applied to vehicle");
      setPartOpen(false);
      setCartItems([]);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!vehicle) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-10 text-sm text-zinc-500">Loading vehicle...</div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-10 max-w-7xl">
      <Link to="/vehicles" className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-600 hover:text-zinc-950 mb-4" data-testid="back-to-vehicles">
        <ArrowLeft className="w-3 h-3" /> Back to vehicles
      </Link>

      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 pb-6 border-b border-zinc-200">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500 mb-2 font-mono">
            {vehicle.vin}
          </div>
          <h1 className="font-heading text-4xl tracking-tighter font-black text-zinc-950" data-testid="vehicle-title">
            {(vehicle.title && vehicle.title.trim()) || `${vehicle.year} ${vehicle.make} ${vehicle.model}`}
          </h1>
          {vehicle.title && vehicle.title.trim() && (
            <div className="mt-1 text-xs font-mono text-zinc-500">
              {vehicle.year} {vehicle.make} {vehicle.model}
            </div>
          )}
          <div className="mt-1 text-sm text-zinc-600">
            {vehicle.color || "—"} · {(vehicle.mileage || 0).toLocaleString()} mi
            {vehicle.engine_type === "na" && <> · <span className="font-bold text-sky-700">NA</span></>}
            {vehicle.engine_type === "turbo" && <> · <span className="font-bold text-rose-700">Turbo</span></>}
            {vehicle.transmission === "manual" && <> · <span className="font-bold">Manual</span></>}
            {vehicle.transmission === "automatic" && <> · <span className="font-bold">Auto</span></>}
            {vehicle.steering === "lhd" && <> · <span className="font-bold">LHD</span></>}
            {vehicle.steering === "rhd" && <> · <span className="font-bold">RHD</span></>}
            {" · "}
            <span className={(statusMeta[vehicle.status] || statusMeta.in_inventory).className}>
              {(statusMeta[vehicle.status] || statusMeta.in_inventory).label}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setEditing({ ...vehicle })} data-testid="edit-vehicle-btn" className="rounded-sm border-zinc-300">
            Edit
          </Button>
          <Button variant="outline" onClick={onDeleteVehicle} data-testid="delete-vehicle-btn" className="rounded-sm border-red-200 text-red-600 hover:bg-red-50">
            Delete
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <div className="bg-white border border-zinc-200 rounded-sm p-5">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">Purchase</div>
          <div className="font-heading text-2xl font-black mt-2">{currency(vehicle.purchase_price)}</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-5">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">Sale</div>
          <div className="font-heading text-2xl font-black mt-2">{vehicle.sale_price != null ? currency(vehicle.sale_price) : "—"}</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-5">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">Total Expenses</div>
          <div className="font-heading text-2xl font-black mt-2" data-testid="vehicle-expenses-total">{currency(vehicle.total_expenses)}</div>
        </div>
        <div className="bg-white border border-zinc-200 rounded-sm p-5">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">Profit / Loss</div>
          <div className={`font-heading text-2xl font-black mt-2 ${vehicle.profit >= 0 ? "text-[#10B981]" : "text-[#FF2400]"}`} data-testid="vehicle-profit">
            {currency(vehicle.profit)}
          </div>
        </div>
      </div>

      {vehicle.notes && (
        <div className="mt-6 bg-white border border-zinc-200 rounded-sm p-5" data-testid="vehicle-notes-display">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-zinc-500">Notes</div>
          <div className="text-sm text-zinc-800 whitespace-pre-wrap mt-2">{vehicle.notes}</div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        {/* Expenses */}
        <section className="bg-white border border-zinc-200 rounded-sm">
          <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
            <h2 className="font-heading text-lg font-bold">Expenses Log</h2>
            <Dialog open={expOpen} onOpenChange={setExpOpen}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="add-expense-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Expense
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm">
                <DialogHeader>
                  <DialogTitle className="font-heading">New expense</DialogTitle>
                </DialogHeader>
                <form onSubmit={addExpense} className="space-y-4" data-testid="expense-form">
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">{svcLabel.singular} (optional)</Label>
                    <Select value={expForm.service_id || "__none__"} onValueChange={onServiceSelect}>
                      <SelectTrigger className="rounded-sm border-zinc-300" data-testid="expense-service-select">
                        <SelectValue placeholder={`Select a ${svcLabel.singularLower}...`} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— None —</SelectItem>
                        {services.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name} — {currency(s.default_cost)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {services.length === 0 && (
                      <div className="text-xs text-zinc-500 mt-1">
                        No {svcLabel.pluralLower} yet.{" "}
                        <Link to="/services" className="font-semibold text-[#002FA7] hover:underline">
                          Add one
                        </Link>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Description</Label>
                    <Input required value={expForm.description} onChange={(e) => setExpForm({ ...expForm, description: e.target.value })} data-testid="expense-desc-input" className="rounded-sm border-zinc-300" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Amount</Label>
                      <Input required type="number" step="0.01" value={expForm.amount} onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} data-testid="expense-amount-input" className="rounded-sm border-zinc-300 font-mono" />
                    </div>
                    <div>
                      <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Category</Label>
                      <Select value={expForm.category} onValueChange={(v) => setExpForm({ ...expForm, category: v })}>
                        <SelectTrigger className="rounded-sm border-zinc-300"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General</SelectItem>
                          <SelectItem value="part">Parts</SelectItem>
                          <SelectItem value="labor">Labor</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="submit" data-testid="expense-save-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">Add</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="max-h-[28rem] overflow-y-auto">
            {expenses.length === 0 && (
              <div className="px-6 py-8 text-sm text-zinc-500">No expenses yet.</div>
            )}
            {expenses.length > 0 && EXPENSE_GROUPS.map((g) => {
              const rows = expenses.filter(g.match);
              const subtotal = rows.reduce((s, e) => s + Number(e.amount || 0), 0);
              return (
                <div key={g.key} data-testid={`expense-group-${g.key}`}>
                  <div className="px-6 py-3 bg-zinc-50 border-b border-t border-zinc-200 flex items-center justify-between">
                    <div className="font-heading text-sm font-bold uppercase tracking-wider text-zinc-900">
                      {g.label} <span className="text-zinc-400 font-normal ml-1">· {rows.length}</span>
                    </div>
                    <div className="text-sm font-mono font-bold text-zinc-900" data-testid={`expense-subtotal-${g.key}`}>
                      {currency(subtotal)}
                    </div>
                  </div>
                  {rows.length === 0 ? (
                    <div className="px-6 py-3 text-xs text-zinc-400">— none —</div>
                  ) : (
                    <div className="divide-y divide-zinc-200">
                      {rows.map((e) => (
                        <div key={e.id} className="px-6 py-3 flex items-center justify-between" data-testid={`expense-row-${e.id}`}>
                          <div className="min-w-0">
                            <div className="text-sm text-zinc-950 truncate">{e.description}</div>
                            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mt-0.5">
                              {e.service_name || e.category}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-sm font-mono font-bold text-zinc-900">{currency(e.amount)}</div>
                            <button onClick={() => deleteExpense(e.id)} className="text-zinc-400 hover:text-[#FF2400]" data-testid={`delete-expense-${e.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Part Usage */}
        <section className="bg-white border border-zinc-200 rounded-sm">
          <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
            <h2 className="font-heading text-lg font-bold">Parts Used</h2>
            <Dialog open={partOpen} onOpenChange={(o) => { setPartOpen(o); if (!o) setCartItems([]); }}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="use-parts-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">
                  <Wrench className="w-3.5 h-3.5 mr-1" /> Use Part
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-xl rounded-sm">
                <DialogHeader>
                  <DialogTitle className="font-heading">Use parts on this vehicle</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Part</Label>
                      <Select value={selectedPart} onValueChange={setSelectedPart}>
                        <SelectTrigger className="rounded-sm border-zinc-300" data-testid="use-part-select">
                          <SelectValue placeholder="Select a part..." />
                        </SelectTrigger>
                        <SelectContent>
                          {parts.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name} ({p.sku}) — {p.quantity <= 0 ? "Out of stock" : `${p.quantity} in stock`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-24">
                      <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Qty</Label>
                      <Input type="number" min={1} value={partQty} onChange={(e) => setPartQty(e.target.value)} data-testid="use-part-qty" className="rounded-sm border-zinc-300 font-mono" />
                    </div>
                    <Button type="button" onClick={addToCart} variant="outline" data-testid="cart-add-btn" className="rounded-sm border-zinc-300">Add</Button>
                  </div>

                  {cartItems.length > 0 && (
                    <div className="border border-zinc-200 rounded-sm">
                      {cartItems.map((i, idx) => (
                        <div key={i.part_id} className="px-4 py-2 flex justify-between items-center border-b border-zinc-200 last:border-0">
                          <div>
                            <div className="text-sm font-semibold">{i.name}</div>
                            <div className="text-xs text-zinc-500 font-mono">{i.sku} · x{i.quantity}</div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-sm font-mono">{currency(i.quantity * i.unit_cost)}</div>
                            <button
                              onClick={() => setCartItems(cartItems.filter((_, j) => j !== idx))}
                              className="text-zinc-400 hover:text-[#FF2400]"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                      <div className="px-4 py-2 bg-zinc-50 flex justify-between text-sm font-bold">
                        <span>Total</span>
                        <span className="font-mono">
                          {currency(cartItems.reduce((s, i) => s + i.quantity * i.unit_cost, 0))}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setPartOpen(false)} className="rounded-sm">Cancel</Button>
                  <Button onClick={submitUsage} data-testid="apply-parts-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">
                    Apply to Vehicle
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="divide-y divide-zinc-200 max-h-96 overflow-y-auto">
            {usages.length === 0 && (
              <div className="px-6 py-8 text-sm text-zinc-500 flex items-center gap-2">
                <Package className="w-4 h-4" /> No parts used yet.
              </div>
            )}
            {usages.map((u) => (
              <div key={u.id} className="px-6 py-3 flex items-center justify-between" data-testid={`usage-row-${u.id}`}>
                <div>
                  <div className="text-sm font-semibold text-zinc-950">{u.part_name}</div>
                  <div className="text-xs text-zinc-500 font-mono">{u.sku} · x{u.quantity}</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-sm font-mono font-bold text-zinc-900">{currency(u.total_cost)}</div>
                  <button
                    onClick={() => deletePartUsage(u)}
                    data-testid={`delete-usage-${u.id}`}
                    title="Remove & restore to inventory"
                    className="text-zinc-400 hover:text-[#FF2400]"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-zinc-200 flex-shrink-0">
            <DialogTitle className="font-heading">Edit vehicle</DialogTitle>
          </DialogHeader>
          {editing && (
            <form onSubmit={onEditSave} className="flex flex-col flex-1 min-h-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-6 py-4 overflow-y-auto flex-1">
              <div className="col-span-2">
                <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">VIN</Label>
                <Input value={editing.vin} onChange={(e) => setEditing({ ...editing, vin: e.target.value.toUpperCase() })} className="rounded-sm border-zinc-300 font-mono" />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">
                  Display Title <span className="text-zinc-400 normal-case tracking-normal font-normal">(optional — overrides &quot;Year Make Model&quot;)</span>
                </Label>
                <Input
                  value={editing.title || ""}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  placeholder={`e.g. "Project Turbo"`}
                  data-testid="vehicle-title-edit"
                  className="rounded-sm border-zinc-300"
                />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Make</Label>
                <Input value={editing.make} onChange={(e) => setEditing({ ...editing, make: e.target.value })} className="rounded-sm border-zinc-300" />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Model</Label>
                <Input value={editing.model} onChange={(e) => setEditing({ ...editing, model: e.target.value })} className="rounded-sm border-zinc-300" />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Year</Label>
                <Input type="number" value={editing.year} onChange={(e) => setEditing({ ...editing, year: e.target.value })} className="rounded-sm border-zinc-300 font-mono" />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Mileage</Label>
                <Input type="number" value={editing.mileage || 0} onChange={(e) => setEditing({ ...editing, mileage: e.target.value })} className="rounded-sm border-zinc-300 font-mono" />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Color</Label>
                <Input value={editing.color || ""} onChange={(e) => setEditing({ ...editing, color: e.target.value })} className="rounded-sm border-zinc-300" />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Engine</Label>
                <Select value={editing.engine_type || "none"} onValueChange={(v) => setEditing({ ...editing, engine_type: v === "none" ? "" : v })}>
                  <SelectTrigger className="rounded-sm border-zinc-300" data-testid="vehicle-edit-engine-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    <SelectItem value="na">NA / Non Turbo</SelectItem>
                    <SelectItem value="turbo">Turbo / Twin Turbo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Transmission</Label>
                <Select value={editing.transmission || "none"} onValueChange={(v) => setEditing({ ...editing, transmission: v === "none" ? "" : v })}>
                  <SelectTrigger className="rounded-sm border-zinc-300" data-testid="vehicle-edit-transmission-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="automatic">Automatic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Steering</Label>
                <Select value={editing.steering || "none"} onValueChange={(v) => setEditing({ ...editing, steering: v === "none" ? "" : v })}>
                  <SelectTrigger className="rounded-sm border-zinc-300" data-testid="vehicle-edit-steering-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    <SelectItem value="lhd">LHD</SelectItem>
                    <SelectItem value="rhd">RHD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Status</Label>
                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger className="rounded-sm border-zinc-300"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_inventory">In Inventory</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="sold">Sold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">List Month</Label>
                <Input
                  type="month"
                  value={editing.list_month || ""}
                  onChange={(e) => setEditing({ ...editing, list_month: e.target.value })}
                  data-testid="vehicle-list-month-edit"
                  className="rounded-sm border-zinc-300 font-mono"
                />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Purchase Price</Label>
                <Input type="number" step="0.01" value={editing.purchase_price} onChange={(e) => setEditing({ ...editing, purchase_price: e.target.value })} className="rounded-sm border-zinc-300 font-mono" />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Sale Price</Label>
                <Input type="number" step="0.01" value={editing.sale_price ?? ""} onChange={(e) => setEditing({ ...editing, sale_price: e.target.value })} className="rounded-sm border-zinc-300 font-mono" />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Sold Date</Label>
                <Input
                  type="date"
                  value={editing.sold_date || ""}
                  onChange={(e) => setEditing({ ...editing, sold_date: e.target.value })}
                  data-testid="vehicle-sold-date-edit"
                  className="rounded-sm border-zinc-300 font-mono"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-600">Notes</Label>
                <Textarea value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} data-testid="vehicle-notes-edit" className="rounded-sm border-zinc-300" rows={3} />
              </div>
              </div>
              <DialogFooter className="px-6 py-4 border-t border-zinc-200 bg-white flex-shrink-0">
                <Button type="button" variant="outline" onClick={() => setEditing(null)} className="rounded-sm">Cancel</Button>
                <Button type="submit" data-testid="vehicle-edit-save" className="rounded-sm bg-[#002FA7] hover:bg-[#002075] font-semibold">Save</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
